type Frame = string | ArrayBuffer;
export type DecodedFrame =
  | { kind: "decoded"; raw: string }
  | { kind: "error"; error: unknown }
  | { kind: "discarded" };
type Job = {
  data: Frame;
  current: () => boolean;
  settle: (result: DecodedFrame) => void;
};

/** Start independent decompression promptly; the connection still dispatches
 * results in arrival order. Settled error values prevent a later frame's early
 * rejection from becoming unhandled while an earlier frame is decoding.
 */
export class FrameDecoder {
  private active = 0;
  private pending: Job[] = [];

  constructor(private readonly decode: (data: Frame) => Promise<string>) {}

  enqueue(data: Frame, current: () => boolean): Promise<DecodedFrame> {
    const result = new Promise<DecodedFrame>((settle) => {
      this.pending.push({ data, current, settle });
    });
    this.pump();
    return result;
  }

  discardStale(): void {
    this.pending = this.pending.filter((job) => {
      if (job.current()) return true;
      job.settle({ kind: "discarded" });
      return false;
    });
  }

  private pump(): void {
    while (this.active < 4 && this.pending.length) {
      const job = this.pending.shift()!;
      if (!job.current()) {
        job.settle({ kind: "discarded" });
        continue;
      }
      ++this.active;
      // Invoke inside a promise so both synchronous throws and asynchronous
      // failures are observed immediately, independently of ordered dispatch.
      void Promise.resolve()
        .then(() => (job.current() ? this.decode(job.data) : undefined))
        .then(
          (raw) =>
            this.finish(
              job,
              raw === undefined
                ? { kind: "discarded" }
                : { kind: "decoded", raw },
            ),
          (error) => this.finish(job, { kind: "error", error }),
        );
    }
  }

  private finish(job: Job, result: DecodedFrame): void {
    --this.active;
    job.settle(result);
    this.pump();
  }
}
